import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Logger from './logger';
import ErrorModal from './ErrorModal';

/**
 * ErrorBoundary — GL2026 crash shield
 *
 * Wraps any subtree and catches render / lifecycle exceptions.
 *
 * Circuit-breaker:
 *   If ≥ CIRCUIT_THRESHOLD errors are caught within CIRCUIT_WINDOW_MS the
 *   boundary trips (tripped=true) and renders the hard "System Unavailable"
 *   screen instead of offering a Retry, preventing infinite re-render loops.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 */

const CIRCUIT_THRESHOLD = 3;       // max errors before tripping
const CIRCUIT_WINDOW_MS = 10000;   // rolling window (10 s)

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError:  false,
      error:     null,
      tripped:   false,
    };
    /** Timestamps (ms) of recent errors, used for circuit-breaker accounting. */
    this._errorTimestamps = [];
    this.handleRetry = this.handleRetry.bind(this);
  }

  // ── React error-boundary lifecycle ─────────────────────────────────────────

  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // 1. Log to our logger (persisted to localStorage).
    Logger.error(
      'ErrorBoundary caught:',
      error && (error.stack || error.message),
      '| Component stack:',
      info && info.componentStack
    );

    // 2. Circuit-breaker: record this error timestamp.
    const now = Date.now();
    this._errorTimestamps.push(now);

    // Evict timestamps outside the rolling window.
    this._errorTimestamps = this._errorTimestamps.filter(
      ts => now - ts <= CIRCUIT_WINDOW_MS
    );

    if (this._errorTimestamps.length >= CIRCUIT_THRESHOLD) {
      Logger.error(
        'ErrorBoundary circuit-breaker tripped:',
        this._errorTimestamps.length,
        'errors in',
        CIRCUIT_WINDOW_MS / 1000,
        's. Halting re-render.'
      );
      this.setState({ tripped: true });
    }
  }

  // ── body class management ────────────────────────────────────────────────────

  /**
   * Add/remove .gl-error-active on <body> whenever the error state changes.
   * This class is used in CSS to suppress pointer-events on any full-viewport
   * transparent loading overlays (.spinner-wrapper etc.) that sit OUTSIDE the
   * ErrorBoundary scope and would otherwise silently swallow button clicks.
   */
  _syncBodyClass(isErrorActive) {
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('gl-error-active', isErrorActive);
    }
  }

  componentDidUpdate(_, prevState) {
    const wasActive = prevState.hasError || prevState.tripped;
    const isActive  = this.state.hasError || this.state.tripped;
    if (wasActive !== isActive) {
      this._syncBodyClass(isActive);
    }
  }

  componentWillUnmount() {
    // Always clean up the body class when the boundary unmounts.
    this._syncBodyClass(false);
  }

  // ── Retry handler ───────────────────────────────────────────────────────────

  handleRetry() {
    // Only allow retry when the circuit-breaker has NOT tripped.
    if (this.state.tripped) return;
    Logger.info('ErrorBoundary: user requested retry — resetting error state.');
    this.setState({ hasError: false, error: null });
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  render() {
    const { hasError, error, tripped } = this.state;

    if (hasError || tripped) {
      /**
       * Render via a React Portal directly into <body>.
       *
       * WHY: The ErrorModal uses `position: fixed; z-index: 99999`.
       * Even with a high z-index, a `position:fixed` element can be trapped
       * inside a CSS stacking context created by an ancestor (e.g. a styled-
       * component that injects `opacity`, `transform`, or `will-change`).
       * Portalling to <body> guarantees the modal lives outside every ancestor
       * stacking context and its z-index is evaluated at the root/viewport level.
       */
      return ReactDOM.createPortal(
        <ErrorModal
          error={error}
          onRetry={this.handleRetry}
          tripped={tripped}
        />,
        document.body
      );
    }

    return this.props.children;
  }
}
